import React, { useEffect, useState, useCallback } from "react";
import api from "config/axiosConfig";
import UserProfilePlate from "components/User/UserProfilePlate";
import LoadingSkeleton from "ui/LoadingSkeleton";
import EmptyState from "ui/EmptyState";
import { AxiosError } from "axios";
import NotFoundPage from "components/ResponsePages/404NotFoundPage";
import ServerErrorPage from "components/ResponsePages/500ServerErrorPage";
import { getActorDetails, type EntityData } from "util/getObjectDetails";
import { apiRoutes } from "util/apiRoutes";
import type { User } from "util/constants";

/**
 * Project a loosely-typed actor record from the actor endpoint into a User
 * shape suitable for UserProfilePlate. UserProfilePlate runs its own canonical
 * fetch on mount, so unknown fields are seeded with safe defaults and will be
 * overwritten with authoritative data once the component fetches the full user.
 */
function projectActorToUser(actor: EntityData): User {
  const propertiesPublic = (actor.properties_public ?? {}) as Record<string, unknown>;
  return {
    id: actor.id,
    id_short: String(actor.id),
    username: typeof actor.username === "string" ? actor.username : "",
    login_name: "",
    display_name: typeof actor.display_name === "string" ? actor.display_name : "",
    email: "",
    password_weak: null,
    password_last_set_ts: "",
    created_at: "",
    updated_at: "",
    is_active: true,
    is_admin: false,
    auth_type: "",
    last_login_at: "",
    deactivated_at: "",
    deleted_at: "",
    de_reason: null,
    properties: actor.properties ?? {},
    about: null,
    properties_public: {
      avatar_url: typeof propertiesPublic.avatar_url === "string" ? propertiesPublic.avatar_url : "",
      avatar_key: typeof propertiesPublic.avatar_key === "string" ? propertiesPublic.avatar_key : "",
      creator_id:
        typeof propertiesPublic.creator_id === "number" ? propertiesPublic.creator_id : 0,
      is_private:
        typeof propertiesPublic.is_private === "boolean" ? propertiesPublic.is_private : undefined,
    },
  };
}

/**
 * One follow row as returned by the follow API. Loose typing because the
 * backend includes optional bestea/status flags that we don't all consume.
 */
interface FollowEdge {
  follower_id: number;
  follower_type: "user" | "team";
  following_id: number;
  following_type: "user" | "team";
}

interface FollowListProps {
  /** The signed-in viewer whose relationships we are listing. */
  viewerId: number;
  /** The relationship direction we want to render. */
  viewType: "following" | "followers";
  /** Optional empty-state copy override. */
  emptyTitle?: string;
  emptySubtitle?: string;
}

/**
 * List of users that the current viewer follows ("following") or that follow
 * the current viewer ("followers"). Filters to user actors only — team
 * follows are surfaced through the Parteas filter instead.
 *
 * Each row is rendered with the canonical `UserProfilePlate` so this list
 * looks and behaves identically to the "Everyone" tab once a user is
 * selected through the people filter.
 */
const FollowList: React.FC<FollowListProps> = ({
  viewerId,
  viewType,
  emptyTitle,
  emptySubtitle,
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  const fetchFollowList = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const url =
        viewType === "followers"
          ? apiRoutes.follow.followers("user", viewerId)
          : apiRoutes.follow.following("user", viewerId);
      const response = await api.get<FollowEdge[]>(url);

      // Resolve the relevant actor for each follow row, skipping team follows
      // because the people tab filter excludes them from the Users branch.
      const followRows = Array.isArray(response.data) ? response.data : [];
      const userEdges = followRows.filter((row) => {
        const targetType = viewType === "followers" ? row.follower_type : row.following_type;
        return targetType === "user";
      });

      const enriched = await Promise.all(
        userEdges.map(async (row) => {
          const targetId = viewType === "followers" ? row.follower_id : row.following_id;
          const actor = await getActorDetails("user", targetId);
          return projectActorToUser(actor);
        })
      );

      // Defensive de-dup on user id in case the backend returns duplicates
      // across paginated batches in the future.
      const seen = new Set<number>();
      const unique: User[] = [];
      enriched.forEach((user) => {
        if (user && typeof user.id === "number" && !seen.has(user.id)) {
          seen.add(user.id);
          unique.push(user);
        }
      });

      setUsers(unique);
    } catch (caught) {
      if (caught instanceof AxiosError && caught.response?.status === 404) {
        setError("404");
      } else {
        setError("server");
      }
    } finally {
      setLoading(false);
    }
  }, [viewerId, viewType]);

  useEffect(() => {
    fetchFollowList();
  }, [fetchFollowList]);

  if (loading) {
    return <LoadingSkeleton variant="actor-card" avatarSize="h-16 w-16" count={5} />;
  }

  if (error === "404") {
    return (
      <NotFoundPage
        displayStyle="page"
        errorMessage="Sorry, we couldn't load that follow list."
      />
    );
  }

  if (error === "server") {
    return <ServerErrorPage />;
  }

  if (users.length === 0) {
    return (
      <EmptyState
        title={
          emptyTitle ??
          (viewType === "followers" ? "No followers yet" : "Not following anyone yet")
        }
        subtitle={
          emptySubtitle ??
          (viewType === "followers"
            ? "When other users follow you, they'll show up here."
            : "Users you follow will show up here.")
        }
        className="p-4"
      />
    );
  }

  return (
    <div className="dark:bg-eoe-dark-bg overflow-x-hidden">
      <div className="entity-divider flex flex-col">
        {users.map((user) => (
          <div key={`follow-user-${user.id}`}>
            <UserProfilePlate user={user} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default FollowList;
